import 'dayjs/locale/en-gb';

import { useMutation } from '@apollo/client/react';
import {
  AddCommentOutlined,
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
  LockOutlined,
  PeopleAltOutlined,
  ViewCompactOutlined,
} from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useContext, useState } from 'react';

import DiaryEntryModal from '../ReportComponents/DiaryEntry/DiaryEntryModal';
import RequestPermissionTooltip from '../RequestPermissionTooltip';

import WeatherTelemetryV2 from './WeatherTelemetryV2';

import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import { PermissionActionEnum, PermissionEntityEnum, SubmitReportDocument } from '@/graphql/generated/graphql';
import { arrayLength } from '@/utils/helperFunctions';
import { isDateWholeDayHoliday, parseHolidays } from '@/utils/holidayHelpers';
import { displayNotification } from '@/utils/notifications';

dayjs.extend(utc);

interface HeaderSectionV2Props {
  onToggleClassicView?: () => void;
}

const HeaderSectionV2: React.FC<HeaderSectionV2Props> = ({ onToggleClassicView }) => {
  const { projects, selectedProject, selectedReport, onUpdate, isWeekendsEnabled, projectHolidays } =
    useContext(ReportContext);
  const { can, canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const [openDiaryModal, setOpenDiaryModal] = useState(false);

  const parsedHolidays = parseHolidays(projectHolidays);

  const isDateDisabled = (date: Dayjs) => {
    if (isWeekendsEnabled) {
      const day = date.day();
      if (day === 0 || day === 6) return true;
    }
    if (isDateWholeDayHoliday(parsedHolidays, date)) {
      return true;
    }
    return false;
  };

  const handleDateSelection = (selectedDate: Dayjs | null) => {
    if (!selectedDate || !selectedDate.isValid()) return;
    onUpdate(selectedDate.format('DD/MM/YYYY'), 'date');
  };

  const handleStepDate = (direction: -1 | 1) => {
    const baseDate = selectedReport?.reportDate ? dayjs(selectedReport.reportDate as unknown as string) : dayjs();
    let candidate = baseDate.add(direction, 'day');
    let safety = 0;
    while (isDateDisabled(candidate) && safety < 14) {
      candidate = candidate.add(direction, 'day');
      safety += 1;
    }
    onUpdate(candidate.format('DD/MM/YYYY'), 'date');
  };

  const [submitReport, { loading: submitting }] = useMutation(SubmitReportDocument);

  const handleSubmit = () => {
    if (!selectedReport?.id) return;
    submitReport({
      variables: { reportId: selectedReport.id },
      onCompleted: () => {
        displayNotification({
          type: 'success',
          text: 'Report submitted successfully',
        });
        onUpdate(dayjs().add(1, 'day').format('DD/MM/YYYY'), 'date');
      },
      onError: () =>
        displayNotification({
          type: 'error',
          text: 'Failed to submit report',
        }),
    });
  };

  const hasSubmitPermission = can(PermissionActionEnum.Create, PermissionEntityEnum.Reports, {
    projectId: selectedProject?.id,
  });
  const isReportOpen = selectedReport?.stage === 'open';
  const disabledSubmit = !hasSubmitPermission || !isReportOpen || submitting;
  const canCreateDiary = canPerformAction(PermissionActionEnum.Create, PermissionEntityEnum.Events);

  if (!projects || !selectedProject) return <></>;

  const managers = selectedProject.managers || [];
  const managersTooltipContent =
    arrayLength(managers) > 0 ? (
      <Box sx={{ p: 0.75 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: '#F8FAFC',
            letterSpacing: '0.04em',
            display: 'block',
            mb: 0.75,
          }}
        >
          PROJECT MANAGERS ({arrayLength(managers)})
        </Typography>
        <Stack spacing={0.75}>
          {managers.map((mgr) => (
            <Stack key={`mgr-${mgr.id}`} direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: '0.7rem',
                  backgroundColor: '#1D4ED8',
                }}
              >
                {mgr.companyResource?.name?.[0] || 'M'}
              </Avatar>
              <Typography variant="caption" sx={{ color: '#E2E8F0' }}>
                {mgr.companyResource?.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    ) : (
      'No Managers Assigned'
    );

  return (
    <Box
      data-testid="report-header"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E2E8F0',
        px: 2.5,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
      }}
    >
      {/* Left: Project Selector + Managers + Date Stepper */}
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Autocomplete
          key={`project-selector-${selectedProject.id}`}
          id="project-unified-selector"
          disablePortal
          disableClearable
          value={selectedProject}
          options={projects}
          onChange={(_, newValue) => onUpdate(newValue, 'project')}
          getOptionLabel={(option) => `${option.code} — ${option.title}`}
          sx={{
            width: { xs: 260, md: 320 },
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#F8FAFC',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#0F172A',
              borderRadius: 1.5,
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
            },
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={`proj-opt-${option.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '0.8125rem',
              }}
            >
              <Chip
                label={option.code}
                size="small"
                sx={{
                  height: 20,
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  backgroundColor: '#E2E8F0',
                  color: '#0F172A',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                {option.title}
              </Typography>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Project" size="small" />}
        />

        {/* Managers Badge */}
        <Tooltip title={managersTooltipContent} arrow placement="bottom">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              height: 36,
              borderRadius: 1.5,
              backgroundColor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              cursor: 'default',
            }}
          >
            {arrayLength(managers) > 0 ? (
              <AvatarGroup
                max={3}
                sx={{
                  '& .MuiAvatar-root': {
                    width: 22,
                    height: 22,
                    fontSize: '0.68rem',
                    borderColor: '#FFFFFF',
                  },
                }}
              >
                {managers.map((mgr) => (
                  <Avatar key={`hdr-mgr-${mgr.id}`} sx={{ backgroundColor: '#1E293B' }}>
                    {mgr.companyResource?.name?.[0] || 'M'}
                  </Avatar>
                ))}
              </AvatarGroup>
            ) : (
              <PeopleAltOutlined sx={{ fontSize: 16, color: '#64748B' }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
              {arrayLength(managers)} {arrayLength(managers) === 1 ? 'Manager' : 'Managers'}
            </Typography>
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />

        {/* 1-Click Date Stepper & Picker */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Previous Working Day">
            <IconButton
              size="small"
              onClick={() => handleStepDate(-1)}
              sx={{
                width: 32,
                height: 36,
                borderRadius: 1.5,
                border: '1px solid #E2E8F0',
                backgroundColor: '#F8FAFC',
                color: '#334155',
                '&:hover': { backgroundColor: '#F1F5F9' },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              key={`report-date-v2-${selectedReport?.id}`}
              label="Report Date"
              value={selectedReport?.reportDate ? dayjs(selectedReport.reportDate as unknown as string) : dayjs()}
              onChange={(newValue) => handleDateSelection(newValue as Dayjs | null)}
              shouldDisableDate={(date) => isDateDisabled(date as Dayjs)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 156,
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      backgroundColor: '#F8FAFC',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      borderRadius: 1.5,
                      '& fieldset': { borderColor: '#E2E8F0' },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          <Tooltip title="Next Working Day">
            <IconButton
              size="small"
              onClick={() => handleStepDate(1)}
              sx={{
                width: 32,
                height: 36,
                borderRadius: 1.5,
                border: '1px solid #E2E8F0',
                backgroundColor: '#F8FAFC',
                color: '#334155',
                '&:hover': { backgroundColor: '#F1F5F9' },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <WeatherTelemetryV2 />
      </Stack>

      {/* Right: Status Badge + Primary Actions */}
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {selectedReport && (
          <Chip
            icon={
              isReportOpen ? (
                <CheckCircleOutline sx={{ fontSize: '14px !important' }} />
              ) : (
                <LockOutlined sx={{ fontSize: '14px !important' }} />
              )
            }
            label={isReportOpen ? 'STAGE: OPEN' : `STAGE: ${(selectedReport.stage || 'SUBMITTED').toUpperCase()}`}
            size="small"
            sx={{
              height: 28,
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.04em',
              borderRadius: 1,
              backgroundColor: isReportOpen ? '#F0FDF4' : '#F1F5F9',
              color: isReportOpen ? '#15803D' : '#475569',
              border: '1px solid',
              borderColor: isReportOpen ? '#BBF7D0' : '#CBD5E1',
              '& .MuiChip-icon': {
                color: 'inherit',
              },
            }}
          />
        )}

        {onToggleClassicView && (
          <Tooltip title="Switch to Classic Report View">
            <Button
              size="small"
              variant="text"
              startIcon={<ViewCompactOutlined fontSize="small" />}
              onClick={onToggleClassicView}
              sx={{
                height: 36,
                textTransform: 'none',
                color: '#64748B',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.25,
                '&:hover': { backgroundColor: '#F1F5F9', color: '#0F172A' },
              }}
            >
              Classic UI
            </Button>
          </Tooltip>
        )}

        <RequestPermissionTooltip
          hasPermission={canCreateDiary}
          requiredPermission={{
            action: PermissionActionEnum.Create,
            entity: PermissionEntityEnum.Events,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCommentOutlined fontSize="small" />}
              disabled={!canCreateDiary}
              onClick={() => setOpenDiaryModal(true)}
              sx={{
                height: 36,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                borderRadius: 1.5,
                borderColor: '#CBD5E1',
                color: '#0F172A',
                backgroundColor: '#FFFFFF',
                px: 1.75,
                '&:hover': {
                  borderColor: '#94A3B8',
                  backgroundColor: '#F8FAFC',
                },
              }}
            >
              Diary Entry
            </Button>
          </span>
        </RequestPermissionTooltip>

        <RequestPermissionTooltip
          hasPermission={hasSubmitPermission}
          requiredPermission={{
            action: PermissionActionEnum.Create,
            entity: PermissionEntityEnum.Reports,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        >
          <span>
            <Tooltip title={isReportOpen ? 'Submit Daily Site Report' : 'Report is not in open stage'}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={disabledSubmit}
                  onClick={handleSubmit}
                  sx={{
                    height: 36,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    borderRadius: 1.5,
                    px: 2,
                    backgroundColor: '#15803D',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#166534',
                      boxShadow: '0 2px 4px rgba(21, 128, 61, 0.25)',
                    },
                  }}
                >
                  Submit Report
                </Button>
              </span>
            </Tooltip>
          </span>
        </RequestPermissionTooltip>
      </Stack>

      {openDiaryModal && <DiaryEntryModal handleClose={() => setOpenDiaryModal(false)} />}
    </Box>
  );
};

export default HeaderSectionV2;
